import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth";
import { Icon } from "../components/Icon";
import type { AccountSession, AccountSummary } from "../lib/api";
import { localizedError } from "../lib/localized-error";

export function AccountPage() {
  const { t, i18n } = useTranslation();
  const { api, logout, openAuth, session } = useAuth();
  const [account, setAccount] = useState<AccountSummary | null>(null);
  const [sessions, setSessions] = useState<AccountSession[]>([]);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [linkChallenge, setLinkChallenge] = useState<{
    provider: "google" | "github";
    challengeId: string;
  } | null>(null);
  const [linkCode, setLinkCode] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session?.user) return;
    let active = true;
    void Promise.all([api.getMe(), api.accountSessions()])
      .then(([result, sessionRows]) => { if (active) { setAccount(result); setSessions(sessionRows); } })
      .catch((reason) => { if (active) setError(localizedError(reason, t)); });
    return () => { active = false; };
  }, [api, session?.user, t]);

  async function run(action: string, operation: () => Promise<void>) {
    setBusy(action);
    setError(null);
    setNotice(null);
    try {
      await operation();
    } catch (reason) {
      setError(localizedError(reason, t));
    } finally {
      setBusy(null);
    }
  }

  async function connect(provider: "google" | "github") {
    await run(provider, async () => {
      const result = await api.startOAuthLink(provider);
      setLinkChallenge({ provider, challengeId: result.challenge_id });
      setLinkCode("");
      setNotice(t("account.linkCodeSent", {
        email: account?.user.primary_email ?? session?.user?.email ?? "",
      }));
    });
  }

  async function confirmLink() {
    if (!linkChallenge || linkCode.length !== 6) return;
    await run("link-confirm", async () => {
      const url = await api.confirmOAuthLink(
        linkChallenge.provider,
        linkChallenge.challengeId,
        linkCode,
      );
      window.location.assign(url);
    });
  }

  async function requestDeletionCode() {
    await run("delete-start", async () => {
      const result = await api.startAccountDeletion();
      setChallengeId(result.challenge_id);
      setNotice(t("account.deleteCodeSent"));
    });
  }

  async function deleteAccount() {
    if (!challengeId || code.length !== 6) return;
    await run("delete", async () => {
      await api.deleteAccount(challengeId, code);
      await logout().catch(() => undefined);
      window.location.assign("/");
    });
  }

  async function revokeSession(id: string) {
    await run(`session:${id}`, async () => {
      await api.revokeAccountSession(id);
      setSessions((current) => current.filter((sessionRow) => sessionRow.id !== id));
      setNotice(t("account.sessionRevoked"));
    });
  }

  if (!session?.user) {
    return <div className="page account-page"><header className="page-title"><div><p className="eyebrow">{t("account.eyebrow")}</p><h1>{t("account.title")}</h1></div></header><section className="panel account-guest"><span className="account-mark"><Icon name="settings" /></span><h2>{t("account.signInTitle")}</h2><p>{t("account.signInHint")}</p><button className="button button--primary" onClick={openAuth}>{t("dashboard.signIn")}</button></section></div>;
  }

  const google = account?.external_identities.find((identity) => identity.provider === "google");
  const github = account?.external_identities.find((identity) => identity.provider === "github");
  return <div className="page account-page">
    <header className="page-title"><div><p className="eyebrow">{t("account.eyebrow")}</p><h1>{t("account.title")}</h1></div><button className="button button--ghost" onClick={() => void logout()}>{t("auth.logout")}</button></header>
    {error && <p className="form-error" role="alert">{error}</p>}
    {notice && <p className="account-notice" role="status"><Icon name="check" />{notice}</p>}
    <div className="account-layout">
      <section className="panel account-primary">
        <header><span className="account-avatar">{initials(account?.user.display_name || account?.user.primary_email || session.user.email)}</span><div><p className="eyebrow">{t("account.profile")}</p><h2>{account?.user.display_name || session.user.display_name || t("app.name")}</h2><span>{account?.user.primary_email || session.user.email}</span></div><em><Icon name="check" />{t("account.verified")}</em></header>
        <dl className="account-facts">
          <div><dt>{t("account.workspace")}</dt><dd>{account?.workspace?.name ?? t("account.personalWorkspace")}</dd></div>
          <div><dt>{t("account.plan")}</dt><dd>{(account?.workspace?.plan_code ?? "free").toUpperCase()}</dd></div>
          <div><dt>{t("account.memberSince")}</dt><dd>{formatDate(account?.user.created_at_ms, i18n.language)}</dd></div>
          <div><dt>{t("account.lastLogin")}</dt><dd>{formatDate(account?.user.last_login_at_ms, i18n.language)}</dd></div>
        </dl>
      </section>

      <section className="panel account-section">
        <header><div><p className="eyebrow">{t("account.signInMethods")}</p><h2>{t("account.connectedAccounts")}</h2></div></header>
        <IdentityRow provider="Google" email={google?.provider_email} connected={Boolean(google)} busy={busy === "google"} onConnect={() => void connect("google")} />
        <IdentityRow provider="GitHub" email={github?.provider_email} connected={Boolean(github)} busy={busy === "github"} onConnect={() => void connect("github")} />
        {linkChallenge && <div className="oauth-link-confirmation" role="dialog" aria-label={t("account.verifyLink")}>
          <p>{t("account.linkCodeSent", { email: account?.user.primary_email ?? session.user.email })}</p>
          <label><span>{t("account.linkCode")}</span><input autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={linkCode} onChange={(event) => setLinkCode(event.target.value.replaceAll(/\D/gu, "").slice(0, 6))} /></label>
          <button className="button button--primary" disabled={linkCode.length !== 6 || busy === "link-confirm"} onClick={() => void confirmLink()}>{busy === "link-confirm" ? t("common.loading") : t("account.verifyLink")}</button>
          <button className="text-button" disabled={busy === "link-confirm"} onClick={() => { setLinkChallenge(null); setLinkCode(""); setNotice(null); }}>{t("common.cancel")}</button>
        </div>}
        <p className="account-help">{t("account.linkHint")}</p>
      </section>

      <section className="panel account-section account-data">
        <header><div><p className="eyebrow">{t("account.privacy")}</p><h2>{t("account.yourData")}</h2></div></header>
        <p>{t("account.exportHint")}</p>
        <button className="button button--ghost" disabled={busy === "export"} onClick={() => void run("export", async () => { await api.downloadAccountData(); setNotice(t("account.exportReady")); })}><Icon name="download" />{busy === "export" ? t("common.loading") : t("account.export")}</button>
      </section>

      <section className="panel account-section account-sessions">
        <header><div><p className="eyebrow">{t("account.security")}</p><h2>{t("account.sessions")}</h2></div></header>
        {sessions.map((sessionRow) => <div className="session-row" key={sessionRow.id}><span className="identity-mark"><Icon name="clock" /></span><p><strong>{sessionRow.current ? t("account.currentSession") : t("account.otherSession")}</strong><small>{t("account.lastActive", { date: formatDate(sessionRow.last_seen_at_ms, i18n.language) })}</small></p>{sessionRow.current ? <em>{t("account.current")}</em> : <button className="button button--ghost" disabled={busy === `session:${sessionRow.id}`} onClick={() => void revokeSession(sessionRow.id)}>{t("account.revoke")}</button>}</div>)}
      </section>

      <section className="panel account-section danger-zone">
        <header><div><p className="eyebrow">{t("account.danger")}</p><h2>{t("account.deleteTitle")}</h2></div></header>
        <p>{t("account.deleteHint")}</p>
        {!challengeId ? <button className="button danger-button" disabled={busy === "delete-start"} onClick={() => void requestDeletionCode()}><Icon name="trash" />{busy === "delete-start" ? t("common.loading") : t("account.sendDeleteCode")}</button> : <div className="delete-confirmation"><label><span>{t("account.deleteCode")}</span><input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replaceAll(/\D/gu, "").slice(0, 6))} /></label><button className="button danger-button" disabled={code.length !== 6 || busy === "delete"} onClick={() => void deleteAccount()}>{busy === "delete" ? t("common.loading") : t("account.deleteNow")}</button><button className="text-button" onClick={() => { setChallengeId(null); setCode(""); }}>{t("common.cancel")}</button></div>}
      </section>
    </div>
  </div>;
}

function IdentityRow({ provider, email, connected, busy, onConnect }: { provider: string; email?: string | null; connected: boolean; busy: boolean; onConnect: () => void }) {
  const { t } = useTranslation();
  return <div className="identity-row"><span className="identity-mark">{provider[0]}</span><p><strong>{provider}</strong><small>{connected ? email || t("account.connected") : t("account.notConnected")}</small></p>{connected ? <em><Icon name="check" />{t("account.connected")}</em> : <button className="button button--ghost" disabled={busy} onClick={onConnect}>{busy ? t("common.loading") : t("account.connect")}</button>}</div>;
}

function formatDate(timestamp: number | undefined, locale: string): string {
  return timestamp ? new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(timestamp) : "—";
}

function initials(value: string): string {
  return value.replace(/@.*$/u, "").split(/\s+/u).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
}
